import { FC } from 'react'
import styled from 'styled-components'
import Logo from 'views/components/UI/Logo'

export type LogoutStep = 'invalidating' | 'clearing' | 'redirecting'

interface ILogoutStep {
  description: string
  title: string
}

interface ISessionLogoutTransitionProps {
  step: LogoutStep
}

const STEP_INDEX: Record<LogoutStep, number> = {
  invalidating: 0,
  clearing: 1,
  redirecting: 2,
}

const LOGOUT_STEPS: ILogoutStep[] = [
  {
    title: 'Cerrando la sesión segura',
    description: 'Invalidamos la sesión activa en el servidor.',
  },
  {
    title: 'Protegiendo tus datos',
    description: 'Eliminamos las credenciales de este dispositivo.',
  },
  {
    title: 'Volviendo al acceso',
    description: 'Te redirigimos a la pantalla de inicio de sesión.',
  },
]

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(18, 39, 54, 0.72);
  backdrop-filter: blur(8px);
`

const Card = styled.div`
  width: min(100%, 560px);
  padding: 36px;
  border: 1px solid rgba(182, 194, 214, 0.72);
  border-radius: 32px;
  background: #ffffff;
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.24);
  text-align: center;
`

const Eyebrow = styled.span`
  display: block;
  margin-top: 18px;
  color: #2d749c;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.1em;
`

const Title = styled.h1`
  margin: 16px 0 10px;
  color: #183449;
  font-size: 2rem;
`

const Description = styled.p`
  margin: 0;
  color: #587084;
  font-size: 1rem;
  line-height: 1.6;
`

const Progress = styled.div`
  position: relative;
  width: min(100%, 320px);
  height: 7px;
  margin: 28px auto 24px;
  overflow: hidden;
  border-radius: 999px;
  background: #e8eff5;

  &::after {
    position: absolute;
    top: 0;
    left: -40%;
    width: 40%;
    height: 100%;
    border-radius: inherit;
    background: linear-gradient(90deg, #c6016b, #2d749c);
    content: '';
    animation: secure-logout-progress 1.1s ease-in-out infinite;
  }

  @keyframes secure-logout-progress {
    from {
      transform: translateX(0);
    }

    to {
      transform: translateX(350%);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    &::after {
      left: 30%;
      animation: none;
    }
  }
`

const Steps = styled.ol`
  display: grid;
  gap: 12px;
  margin: 0;
  padding: 0;
  list-style: none;
  text-align: left;
`

const StepItem = styled.li<{ complete: boolean; current: boolean }>`
  display: grid;
  grid-template-columns: 24px 1fr;
  gap: 10px;
  align-items: start;
  color: ${(props) => (props.current || props.complete ? '#183449' : '#8a9aa8')};

  strong,
  span {
    display: block;
  }

  strong {
    font-size: 0.9rem;
  }

  span {
    margin-top: 2px;
    font-size: 0.82rem;
    line-height: 1.45;
  }
`

const StepIndicator = styled.span`
  display: grid;
  width: 22px;
  height: 22px;
  place-items: center;
  border: 1px solid #c7d7e2;
  border-radius: 50%;
  color: #2d749c;
  font-size: 1rem;
  font-weight: 800;
`

const SessionLogoutTransition: FC<ISessionLogoutTransitionProps> = ({ step }) => {
  const activeStepIndex: number = STEP_INDEX[step]

  return (
    <Backdrop aria-label="Cierre de sesión seguro" aria-live="polite" aria-modal="true" role="dialog">
      <Card>
        <Logo width="210px" />
        <Eyebrow>CIERRE SEGURO DE SESIÓN</Eyebrow>
        <Title>Cerrando sesión</Title>
        <Description>Estamos cerrando tu acceso de forma segura. Un instante, por favor.</Description>
        <Progress aria-label="Cierre de sesión en curso" role="progressbar" />
        <Steps aria-label="Pasos del cierre de sesión">
          {LOGOUT_STEPS.map((logoutStep: ILogoutStep, index: number) => {
            const isComplete: boolean = index < activeStepIndex
            const isCurrent: boolean = index === activeStepIndex

            return (
              <StepItem key={logoutStep.title} complete={isComplete} current={isCurrent}>
                <StepIndicator aria-hidden="true">{isComplete ? '✓' : isCurrent ? '•' : ''}</StepIndicator>
                <div>
                  <strong>{logoutStep.title}</strong>
                  <span>{logoutStep.description}</span>
                </div>
              </StepItem>
            )
          })}
        </Steps>
      </Card>
    </Backdrop>
  )
}

export default SessionLogoutTransition
